import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users2, Phone, CheckCheck, UserX, Undo2, Hourglass, Bell } from "lucide-react";
import { walkinApi, WalkinEntrada } from "../../lib/api";
import { useInicio } from "../../lib/permisos";

interface SucursalLigera { id: number; nombre: string; }

function tiempoDesde(iso?: string): string {
  if (!iso) return "";
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "recién";
  if (mins < 60) return `hace ${mins} min`;
  return `hace ${Math.floor(mins / 60)}h ${mins % 60}min`;
}

export default function WalkinPage() {
  const inicio = useInicio();
  const [sucursales, setSucursales] = useState<SucursalLigera[]>([]);
  const [sucId, setSucId] = useState<number | null>(null);
  const [entradas, setEntradas] = useState<WalkinEntrada[]>([]);
  const [cargando, setCargando] = useState(true);
  const [accionando, setAccionando] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/v1/sucursales").then((r) => r.json()).then((d: any[]) => {
      if (!Array.isArray(d)) return;
      const list = d.map((s) => ({ id: s.id, nombre: s.nombre }));
      setSucursales(list);
      if (list.length > 0) setSucId(list[0].id);
    }).catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    if (!sucId) return;
    walkinApi.listar(sucId).then(setEntradas).catch(() => {}).finally(() => setCargando(false));
  }, [sucId]);

  useEffect(() => {
    setCargando(true);
    cargar();
    const id = setInterval(cargar, 12_000);
    return () => clearInterval(id);
  }, [cargar]);

  async function accion(id: number, fn: (id: number) => Promise<unknown>) {
    setAccionando(id);
    try {
      await fn(id);
      cargar();
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setAccionando(null);
    }
  }

  const esperando = entradas.filter((e) => e.estado === "esperando");
  const llamados = entradas.filter((e) => e.estado === "llamado");

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <Link to={inicio} className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800">
            <ArrowLeft className="size-4" /> Dashboard
          </Link>
          {sucursales.length > 1 && (
            <select value={sucId ?? ""} onChange={(e) => setSucId(Number(e.target.value))} className="input text-sm w-auto">
              {sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="font-display text-2xl font-bold text-ink-800 mb-1 flex items-center gap-2">
          <Users2 className="size-6 text-brand-600" /> Lista de espera (walk-in)
        </h1>
        <p className="text-sm text-ink-500 mb-6">
          Clientes que se anotaron escaneando el QR de la puerta, sin reserva previa.
        </p>

        {cargando ? (
          <div className="py-16 text-center text-ink-400">Cargando…</div>
        ) : entradas.length === 0 ? (
          <div className="card p-10 text-center text-ink-400">
            <Hourglass className="size-8 mx-auto mb-2 opacity-40" />
            Nadie anotado ahora mismo.
          </div>
        ) : (
          <div className="space-y-5">
            {llamados.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Bell className="size-3.5" /> Mesa avisada — esperando que lleguen
                </p>
                <div className="space-y-2">
                  {llamados.map((e) => (
                    <div key={e.id} className="card p-4 flex items-center justify-between gap-3 bg-emerald-50/50 border-emerald-200">
                      <div className="min-w-0">
                        <p className="font-semibold text-ink-800 truncate">{e.nombre} <span className="text-ink-400 font-normal">· {e.personas} pers.</span></p>
                        <p className="text-xs text-ink-500 flex items-center gap-1"><Phone className="size-3" /> {e.telefono} · avisado {tiempoDesde(e.llamadoAt ?? undefined)}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button disabled={accionando === e.id} onClick={() => accion(e.id, walkinApi.volverEsperando)}
                          title="Volver a la fila de espera"
                          className="w-9 h-9 rounded-lg border border-ink-200 text-ink-500 hover:bg-ink-50 flex items-center justify-center">
                          <Undo2 className="size-4" />
                        </button>
                        <button disabled={accionando === e.id} onClick={() => accion(e.id, walkinApi.noShow)}
                          title="No llegó"
                          className="w-9 h-9 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center justify-center">
                          <UserX className="size-4" />
                        </button>
                        <button disabled={accionando === e.id} onClick={() => accion(e.id, walkinApi.sentar)}
                          className="px-3 h-9 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5">
                          <CheckCheck className="size-4" /> Sentar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              {llamados.length > 0 && (
                <p className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-2">En espera</p>
              )}
              <div className="space-y-2">
                {esperando.map((e) => (
                  <div key={e.id} className="card p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-brand-50 text-brand-700 font-bold text-sm flex items-center justify-center shrink-0">
                        #{e.posicion}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-ink-800 truncate">{e.nombre} <span className="text-ink-400 font-normal">· {e.personas} pers.</span></p>
                        <p className="text-xs text-ink-500 flex items-center gap-1"><Phone className="size-3" /> {e.telefono} · {tiempoDesde(e.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button disabled={accionando === e.id} onClick={() => accion(e.id, walkinApi.noShow)}
                        title="Se fue / cancelar"
                        className="w-9 h-9 rounded-lg border border-ink-200 text-ink-400 hover:bg-ink-50 flex items-center justify-center">
                        <UserX className="size-4" />
                      </button>
                      <button disabled={accionando === e.id} onClick={() => accion(e.id, walkinApi.llamar)}
                        className="px-3 h-9 rounded-lg text-white text-sm font-medium flex items-center gap-1.5"
                        style={{ background: "#C0392B" }}>
                        <Bell className="size-4" /> Mesa lista
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
